/**
 * Subtle entrance animations.
 *
 * Two opt-in hooks, both set in Liquid:
 *
 *   [data-reveal]                  reveals the element itself
 *   [data-reveal-group="<sel>"]    reveals each descendant matching <sel>,
 *                                  staggered in DOM order. Defaults to direct
 *                                  children when no selector is given.
 *
 * The hidden-until-revealed state lives behind `.reveal-enabled` on <html>,
 * which snippets/scroll-reveal-init.liquid sets before first paint. If this
 * module never boots, that snippet's failsafe strips the class and everything
 * renders normally — content is never left invisible.
 */

const REVEALED = 'is-revealed';
const ITEM_ATTR = 'data-reveal-item';

/** Cards near the fold shouldn't wait for a scroll to appear. */
const OBSERVER_OPTIONS = { rootMargin: '0px 0px -8% 0px', threshold: 0.01 };

/** Cap the stagger so a 16-product grid doesn't take a second to finish. */
const MAX_STAGGER_INDEX = 8;

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * Marks an element as an animation target and starts watching it.
 * @param {Element} element - The element to reveal on scroll.
 * @param {number} [index] - Position within its stagger group.
 */
function watch(element, index = 0) {
  if (element.hasAttribute(ITEM_ATTR)) return;

  element.setAttribute(ITEM_ATTR, '');

  if (index > 0) {
    element.style.setProperty('--reveal-index', String(Math.min(index, MAX_STAGGER_INDEX)));
  }

  observer?.observe(element);
}

/**
 * Collects the animation targets inside a root and begins watching them.
 * @param {ParentNode} root - The subtree to scan.
 */
function scan(root) {
  for (const element of root.querySelectorAll('[data-reveal]')) {
    watch(element);
  }

  for (const group of root.querySelectorAll('[data-reveal-group]')) {
    const selector = group.getAttribute('data-reveal-group');
    const children = selector ? group.querySelectorAll(selector) : group.children;

    Array.from(children).forEach((child, index) => watch(child, index));
  }
}

function init() {
  const root = document.documentElement;

  // The init snippet skips the class when the visitor prefers reduced motion.
  if (!root.classList.contains('reveal-enabled')) return;

  if (!('IntersectionObserver' in window)) {
    root.classList.remove('reveal-enabled');
    return;
  }

  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      entry.target.classList.add(REVEALED);
      // One-shot: re-animating on scroll-back reads as flicker, not polish.
      observer?.unobserve(entry.target);
    }
  }, OBSERVER_OPTIONS);

  // Tells the failsafe in scroll-reveal-init.liquid to stand down.
  root.setAttribute('data-reveal-ready', '');

  scan(document);

  // Sections re-render on variant changes, pagination and in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) scan(event.target);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
